import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { CompanyService } from '../services/company.service';
import { Company } from '../company/company.models';
import { AuthService } from '../services/auth.service';
import { DEFAULT_USER_PLAN, UserPlan } from '../models/user-plan.model';
import {
  buildRoleCapabilitySummary,
  roleDescriptionKey,
  roleNameKey,
  RoleCapabilitySummary,
  RoleDefaultsMap
} from '../utils/role-capabilities.utils';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

type AccountIdentifiers = {
  companyCustomerNumber?: string | null;
  contractNumber?: string | null;
  contractStartDate?: string | Date | null;
  contractEndDate?: string | Date | null;
  plan?: UserPlan | null;
};

@Component({
  selector: 'app-account-settings-account',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NzCardModule,
    NzDescriptionsModule,
  ],
  templateUrl: './account-settings-account.component.html',
  styleUrl: './account-settings-account.component.less'
})
export class AccountSettingsAccountComponent implements OnInit {
  company: Company | null = null;
  identifiers: AccountIdentifiers = {};
  email: string | null = null;
  displayName: string | null = null;
  companyRole: string = 'viewer';
  roleDefaults: RoleDefaultsMap = {};
  showRoleCapabilities = false;

  isCompanyLoading = true;
  isIdentifiersLoading = true;

  constructor(
    private companyService: CompanyService,
    private authService: AuthService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadCompany();
    this.loadIdentifiers();
  }

  get isAccountInfoLoading(): boolean {
    return this.isCompanyLoading || this.isIdentifiersLoading;
  }

  get hasContract(): boolean {
    return !!this.identifiers.contractNumber;
  }

  get planTranslationKey(): string {
    const plan = (this.identifiers.plan || DEFAULT_USER_PLAN).toUpperCase();
    return `PLAN.${plan}`;
  }

  get roleLabelKey(): string {
    return roleNameKey(this.companyRole);
  }

  get roleDescription(): string {
    return this.translate.instant(roleDescriptionKey(this.companyRole));
  }

  get roleCanLabels(): string[] {
    const summary: RoleCapabilitySummary = buildRoleCapabilitySummary(this.roleDefaults, this.companyRole, 6, 2);
    return summary.can.map((key: string) => this.translate.instant(key));
  }

  get roleCannotLabels(): string[] {
    const summary: RoleCapabilitySummary = buildRoleCapabilitySummary(this.roleDefaults, this.companyRole, 6, 2);
    return summary.cannot.map((key: string) => this.translate.instant(key));
  }

  private loadCompany(): void {
    this.isCompanyLoading = true;

    this.companyService.getCompanyDetails()
      .pipe(
        finalize(() => {
          this.isCompanyLoading = false;
        })
      )
      .subscribe({
        next: (company) => {
          this.company = company;
        },
        error: () => {
          this.company = null;
        }
      });
  }

  private loadIdentifiers(): void {
    this.isIdentifiersLoading = true;

    this.authService.getMe()
      .pipe(
        finalize(() => {
          this.isIdentifiersLoading = false;
        })
      )
      .subscribe({
        next: (currentUser) => {
          const companyPlan = currentUser?.company?.plan ?? currentUser?.plan ?? DEFAULT_USER_PLAN;
          this.email = currentUser?.email || null;
          this.displayName = currentUser?.displayName || null;
          this.companyRole = currentUser?.companyRole || 'viewer';
          this.roleDefaults = (currentUser?.roleDefaults || {}) as RoleDefaultsMap;
          this.identifiers = {
            companyCustomerNumber: currentUser?.companyCustomerNumber ?? null,
            contractNumber: currentUser?.contractNumber ?? null,
            contractStartDate: currentUser?.contractStartDate ?? null,
            contractEndDate: currentUser?.contractEndDate ?? null,
            plan: companyPlan,
          };
        },
        error: () => {
          const token = this.authService.getToken();
          const payload = token ? this.authService.decodeToken(token) : null;
          this.email = payload?.email || null;
          this.displayName = payload?.displayName || null;
          this.companyRole = payload?.companyRole || 'viewer';
          this.roleDefaults = {};
          this.identifiers = {
            companyCustomerNumber: null,
            contractNumber: payload?.contractNumber ?? null,
            contractStartDate: payload?.contractStartDate ?? null,
            contractEndDate: payload?.contractEndDate ?? null,
            plan: payload?.plan ?? DEFAULT_USER_PLAN,
          };
        },
      });
  }
}
